/**
 * The Google Drive mark: drawn beside every Drive-held item so a Google file
 * is known at a glance and the row has an anchor. One glyph per kind the
 * stored reference knows (a Doc, a Sheet, Slides, a PDF), the Drive mark for
 * anything else on Drive.
 *
 * Decorative only. Every place that draws it already says "Google Drive" or
 * "Google decides who can open this file" in words, and a Drive link already
 * names its destination, so a screen reader hears it once, from the text.
 *
 * The colors are Google's, owned outside IMAM and the same on both tenants:
 * this file is the one narrow literal-color exception for them (token
 * governance, engineering rules). Drawn only where the row already names Drive
 * as the file's owner; a plain link never carries it.
 */
import type { ComponentType } from "react";

import { googleDriveMarkKind, type GoogleDriveMarkKind } from "@/lib/google-drive-labels";
import { cn } from "@/lib/utils";

type MarkProps = { className?: string };

/** A file page with its corner folded, in the kind's color, and the kind's white motif. */
function Page({ fill, fold, children, className }: MarkProps & { fill: string; fold: string; children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className={className}>
      <path d="M6.5 1.5h8l6 6v13a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2v-17a2 2 0 0 1 2-2z" fill={fill} />
      <path d="M14.5 1.5v4.5a1.5 1.5 0 0 0 1.5 1.5h4.5z" fill={fold} />
      {children}
    </svg>
  );
}

const LINES = (
  <path d="M8 11.5h8v1.5h-8zM8 14.5h8v1.5h-8zM8 17.5h5v1.5h-5z" fill="#fff" />
);

function DocMark({ className }: MarkProps) {
  return <Page fill="#4285f4" fold="#a1c2fa" className={className}>{LINES}</Page>;
}

function SheetMark({ className }: MarkProps) {
  return (
    <Page fill="#0f9d58" fold="#87ceac" className={className}>
      <path d="M7.5 11h9v8h-9zm1.5 1.5v1.75h2.25v-1.75zm3.75 0v1.75h2.25v-1.75zM9 15.75v1.75h2.25v-1.75zm3.75 0v1.75h2.25v-1.75z" fill="#fff" fillRule="evenodd" />
    </Page>
  );
}

function SlidesMark({ className }: MarkProps) {
  return (
    <Page fill="#f4b400" fold="#fada80" className={className}>
      <path d="M7.5 11.5h9v7h-9zm1.5 1.5v4h6v-4z" fill="#fff" fillRule="evenodd" />
    </Page>
  );
}

function PdfMark({ className }: MarkProps) {
  return <Page fill="#ea4335" fold="#f5a29a" className={className}>{LINES}</Page>;
}

/** Google Drive's own triangle, for a Drive file whose kind has no glyph of its own. */
function DriveMark({ className }: MarkProps) {
  return (
    <svg viewBox="0 0 87.3 78" aria-hidden="true" focusable="false" className={className}>
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  );
}

const MARKS: Record<GoogleDriveMarkKind, ComponentType<MarkProps>> = {
  doc: DocMark,
  sheet: SheetMark,
  slides: SlidesMark,
  pdf: PdfMark,
  drive: DriveMark,
};

/** One kind's glyph in its box; `data-drive-mark` names the kind drawn. */
function KindMark({ kind, className }: MarkProps & { kind: GoogleDriveMarkKind }) {
  const Mark = MARKS[kind];
  return <span aria-hidden="true" data-drive-mark={kind} className={cn("inline-grid shrink-0 place-items-center", className)}><Mark className="size-full" /></span>;
}

/* One component per kind, stable across renders, for a slot that takes an icon component (a list row's tile). */
export const GoogleDriveDocMark = ({ className }: MarkProps) => <KindMark kind="doc" className={className} />;
export const GoogleDriveSheetMark = ({ className }: MarkProps) => <KindMark kind="sheet" className={className} />;
export const GoogleDriveSlidesMark = ({ className }: MarkProps) => <KindMark kind="slides" className={className} />;
export const GoogleDrivePdfMark = ({ className }: MarkProps) => <KindMark kind="pdf" className={className} />;
export const GoogleDriveFileMark = ({ className }: MarkProps) => <KindMark kind="drive" className={className} />;

/**
 * The mark for one Drive-held item, from what IMAM stored: its Drive type when
 * the reference recorded one, else what its Drive address says it is. The
 * caller decides the item is Drive's; this only decides which glyph.
 */
export function GoogleDriveMark({ mimeType, url, className }: { mimeType?: string | null; url?: string | null; className?: string }) {
  return <KindMark kind={googleDriveMarkKind(mimeType, url)} className={cn("size-4", className)} />;
}
